import { useEffect, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import api from "../../lib/api";
import { ScrollReveal } from "../Reveal";

const bullets = [
  "30-minute private consultation with a named consultant",
  "Route-eligibility analysis for UK, OCI or visa-on-arrival",
  "Fixed-fee quote for the full application — no surprises",
];

const PAYPAL_BUTTON_STYLE = { layout: "vertical", color: "gold", shape: "rect", label: "pay" };

const RESULT_COPY = {
  success: {
    label: "— Confirmed —",
    labelClass: "text-gold",
    title: "Thank you.",
    body: "Consultation reserved. A confirmation email has been sent and a calendar invite will follow within one business day.",
  },
  pending: {
    label: "— Pending —",
    labelClass: "text-gold",
    title: "Almost there.",
    body: "PayPal is reviewing your payment. We\u2019ve emailed you a receipt and will follow up once it settles.",
  },
  failed: {
    label: "— Not Successful —",
    labelClass: "text-red-300",
    title: "Payment declined.",
    body: "No amount was charged. A confirmation email has been sent with next steps. Please try a different card or contact us at info@thevisaatelier.com.",
  },
};

const PaymentResult = ({ paid, onRetry }) => {
  const copy = RESULT_COPY[paid.kind] || RESULT_COPY.failed;
  return (
    <div className="text-center py-10" data-testid="paypal-success">
      <div className={`font-mono-ui text-sm md:text-base tracking-[0.22em] uppercase mb-4 ${copy.labelClass}`}>{copy.label}</div>
      <div className="font-display italic text-4xl text-ivory">{copy.title}</div>
      <p className="mt-6 text-ivory/60">{copy.body}</p>
      {paid.kind === "failed" && (
        <button
          onClick={onRetry}
          className="mt-8 font-mono-ui text-[11px] tracking-[0.25em] uppercase text-gold hover:text-ivory transition-colors"
          data-testid="paypal-retry"
        >
          Try again ↗
        </button>
      )}
      {paid.order_id && (
        <div className="mt-6 font-mono-ui text-sm md:text-base tracking-[0.22em] uppercase text-gold">
          Ref: {paid.order_id?.slice(0, 10)}…
        </div>
      )}
    </div>
  );
};

const PayerFields = ({ payer, setPayer }) => (
  <div className="space-y-6 mb-8">
    <div>
      <label className="block font-mono-ui text-base tracking-[0.22em] uppercase text-gold mb-3">
        Full name
      </label>
      <input
        className="field-line"
        value={payer.name}
        onChange={(e) => setPayer({ ...payer, name: e.target.value })}
        placeholder="Mr. / Ms."
        data-testid="consult-name"
      />
    </div>
    <div>
      <label className="block font-mono-ui text-base tracking-[0.22em] uppercase text-gold mb-3">
        Email
      </label>
      <input
        type="email"
        className="field-line"
        value={payer.email}
        onChange={(e) => setPayer({ ...payer, email: e.target.value })}
        placeholder="you@example.com"
        data-testid="consult-email"
      />
    </div>
    <div>
      <label className="block font-mono-ui text-base tracking-[0.22em] uppercase text-gold mb-3">
        What would you like to discuss?
      </label>
      <textarea
        rows={3}
        className="field-line resize-none"
        value={payer.notes}
        onChange={(e) => setPayer({ ...payer, notes: e.target.value })}
        placeholder="Which visa, timelines, questions..."
        data-testid="consult-notes"
      />
    </div>
  </div>
);

const CheckoutButtons = ({ config, payer, setPaid }) => {
  const createOrder = async () => {
    try {
      const { data } = await api.post("/paypal/orders", payer);
      return data.id;
    } catch (e) {
      toast.error("Could not initialise payment.");
      throw e;
    }
  };

  const onApprove = async (data) => {
    try {
      const { data: res } = await api.post(`/paypal/orders/${data.orderID}/capture`);
      if (res.kind === "success" || res.status === "COMPLETED") {
        setPaid({ ...res, kind: "success" });
        toast.success("Payment received. A confirmation email is on its way.");
      } else if (res.kind === "pending") {
        setPaid({ ...res, kind: "pending" });
        toast.message("Payment pending — we'll email you once it settles.");
      } else {
        setPaid({ ...res, kind: "failed" });
        toast.error("Payment was not successful. Please try another card.");
      }
    } catch (e) {
      setPaid({ order_id: data.orderID, kind: "failed" });
      toast.error("Payment declined. Please try a different card — a confirmation email has been sent.");
    }
  };

  return (
    <div data-testid="paypal-buttons">
      <PayPalScriptProvider
        options={{ clientId: config.client_id, currency: config.currency, intent: "capture" }}
      >
        <PayPalButtons
          style={PAYPAL_BUTTON_STYLE}
          createOrder={createOrder}
          onApprove={onApprove}
          onError={() => toast.error("PayPal encountered an error.")}
          onCancel={() => toast.message("Payment cancelled.")}
        />
      </PayPalScriptProvider>
      <div className="mt-4 font-mono-ui text-[10px] tracking-[0.25em] uppercase text-ivory/40 text-center">
        {config.mode === "sandbox" ? "Sandbox mode — no live charges" : "Secured payment"}
      </div>
    </div>
  );
};

export default function Consultation() {
  const [config, setConfig] = useState(null);
  const [payer, setPayer] = useState({ name: "", email: "", notes: "" });
  const [paid, setPaid] = useState(null);

  useEffect(() => {
    api.get("/paypal/config").then((r) => setConfig(r.data)).catch(() => setConfig(null));
  }, []);

  const format = (v, c) =>
    new Intl.NumberFormat("en-GB", { style: "currency", currency: c || "GBP", maximumFractionDigits: 0 }).format(Number(v || 0));

  return (
    <section
      id="consultation"
      className="relative bg-navy py-24 md:py-40 border-t border-ivory/10"
      data-testid="consultation-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-8 md:gap-16">
          <ScrollReveal className="col-span-12 md:col-span-6">
            <h2 className="font-display font-light text-ivory text-5xl md:text-7xl leading-[0.9] tracking-tight">
              Book a<br />
              <span className="italic text-gold">consultation.</span>
            </h2>
            <p className="mt-8 max-w-lg text-ivory/60 leading-relaxed">
              A one-off deposit reserves a private call with a senior consultant.
              Fully credited against your application fee should you decide to
              proceed.
            </p>

            <ul className="mt-10 space-y-4 font-cinzel text-base md:text-lg tracking-[0.14em] uppercase text-ivory/75">
              {bullets.map((b) => (
                <li key={b} className="flex items-center gap-4">
                  <span className="text-gold">→</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-12 flex items-baseline gap-4">
              <div className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-ivory/50">Deposit</div>
              <div className="font-display text-5xl md:text-6xl text-gold">
                {config ? format(config.amount, config.currency) : "—"}
              </div>
              <div className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-ivory/50">
                {config?.currency || "GBP"}
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal className="col-span-12 md:col-span-6" delay={0.15}>
            <div className="border border-ivory/10 p-8 md:p-10 bg-navy-800/50">
              <div className="font-mono-ui font-bold text-sm md:text-base tracking-[0.22em] text-gold uppercase mb-6">
                Your details
              </div>

              {paid ? (
                <PaymentResult paid={paid} onRetry={() => setPaid(null)} />
              ) : (
                <>
                  <PayerFields payer={payer} setPayer={setPayer} />
                  {config?.client_id ? (
                    <CheckoutButtons config={config} payer={payer} setPaid={setPaid} />
                  ) : (
                    <div className="text-ivory/50 text-sm text-center py-6">Loading payment…</div>
                  )}
                </>
              )}
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}
